import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import { IconComponent, isKnownIcon } from '@@common';

import { ActivityMetadataModel } from '../../../api/api';
import { ActivityCategories, ActivityTypes } from '../Models/ActivityConstants';
import { IconActivity, IconActivityTrigger } from './Icons';

export { ActivityCategories, ActivityTypes };

type ThemeAwareActivityMetadata = ActivityMetadataModel & { darkIconUrl?: string };

function resolveThemeIconUrl(activityMetadata: ThemeAwareActivityMetadata, isDarkTheme: boolean) {
	if (!isDarkTheme || !activityMetadata.iconUrl) {
		return activityMetadata.iconUrl;
	}

	if (activityMetadata.darkIconUrl) {
		return activityMetadata.darkIconUrl;
	}

	// Connector metadata will progressively supply darkIconUrl. Keep a branded
	// variant here for the current Stripe metadata until that contract is published.
	if (activityMetadata.category?.startsWith('Stripe')) {
		return 'https://cdn.simpleicons.org/stripe/ffffff';
	}

	return activityMetadata.iconUrl;
}

type ActivityIconParams = {
	activityMetadata: ActivityMetadataModel | undefined;
	showTriggerBadge?: boolean;
	triggerBadgeSize?: 'default' | 'small';
	maxWidth?: string;
};

export default function ActivityIcon({ activityMetadata, showTriggerBadge, triggerBadgeSize = 'default', maxWidth }: ActivityIconParams) {
	const theme = useTheme();
	const isTrigger = activityMetadata?.categories?.includes(ActivityCategories.TRIGGER) === true;
	const badgeSize = triggerBadgeSize === 'small' ? 10 : 13;
	const badgeOffset = triggerBadgeSize === 'small' ? -1 : -2;
	const badgeIconSize = triggerBadgeSize === 'small' ? 12 : 16;
	let usesTriggerFallback = false;
	let icon = <IconActivity />;
	const iconUrl = activityMetadata ? resolveThemeIconUrl(activityMetadata as ThemeAwareActivityMetadata, theme.palette.mode === 'dark') : undefined;

	if (activityMetadata) {
		if (iconUrl) {
			icon = <IconComponent iconUrl={iconUrl} preserveAspectRatio maxWidth={maxWidth} />;
		} else if (activityMetadata.iconName && isKnownIcon(activityMetadata.iconName)) {
			icon = <IconComponent iconName={activityMetadata.iconName} preserveAspectRatio maxWidth={maxWidth} />;
		} else if (isTrigger) {
			usesTriggerFallback = true;
			icon = <IconActivityTrigger />;
		}
	}

	const renderedIcon = (
		<Box
			component="span"
			sx={{
				display: 'inline-flex',
				alignItems: 'center',
				justifyContent: 'center',
				maxWidth,
			}}
		>
			{icon}
		</Box>
	);

	if (!showTriggerBadge || !isTrigger || usesTriggerFallback) {
		return renderedIcon;
	}

	return (
		<Box
			component="span"
			sx={{ position: 'relative', display: 'inline-flex', width: '1.5rem', height: '1.5rem', alignItems: 'center', justifyContent: 'center' }}
		>
			{renderedIcon}
			<Box
				component="span"
				sx={{
					position: 'absolute',
					right: badgeOffset,
					top: badgeOffset,
					display: 'inline-flex',
					width: badgeSize,
					height: badgeSize,
					alignItems: 'center',
					justifyContent: 'center',
					borderRadius: '50%',
					// Use the canvas background rather than the surrounding surface so the
					// trigger marker remains visible over both hovered rows and activity icons.
					bgcolor: 'background.default',
					color: 'text.primary',
					border: '1px solid',
					borderColor: 'divider',
					boxShadow: (theme) => `0 0 0 1px ${theme.palette.background.default}`,
				}}
			>
				<IconActivityTrigger sx={{ fontSize: badgeIconSize }} />
			</Box>
		</Box>
	);
}
